import React, { useEffect, useState } from "react";
import { API } from "@/lib/constants";
import { Sparkles, ChevronDown, ChevronUp, Loader2 } from "lucide-react";

// ITEM 18 surface — what a PERSON sees: two things and a recommendation. The eight dimensions
// (the diagnosis) live one click behind. Never a wall of badges.
const STATE_DOT = { good: "bg-emerald-500", stale: "bg-amber-500", unknown: "bg-slate-300" };

const fmtVal = (v) => {
  if (v == null) return "—";
  if (typeof v === "object") return v.name ? `${v.name}${v.met ? " (met)" : " (not met)"}` : "—";
  return String(v).replace(/_/g, " ");
};

export default function RelationshipHeadline({ clientId, embedded = false }) {
  const [h, setH] = useState(null);
  const [dims, setDims] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    fetch(`${API}/relationship/clients/${clientId}/headline`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null)).then((d) => { if (live) { setH(d); setLoading(false); } })
      .catch(() => setLoading(false));
    return () => { live = false; };
  }, [clientId]);

  const expand = async () => {
    setOpen((o) => !o);
    if (!dims) {
      const d = await fetch(`${API}/relationship/clients/${clientId}/dimensions`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null)).catch(() => null);
      setDims(d?.dimensions || {});
    }
  };

  if (loading) return <div className="flex items-center gap-2 py-4 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Reading the relationship…</div>;
  if (!h?.found) return null;

  return (
    <div className={embedded ? "" : "rounded-xl border border-slate-200 bg-white p-5"} data-testid="relationship-headline">
      <div className="grid gap-4 sm:grid-cols-2">
        <div data-testid="headline-next-action">
          <div className="text-xs font-medium uppercase tracking-wide text-slate-400">Next</div>
          <div className="mt-1 text-sm font-medium text-slate-900">{h.next_action || "No next step captured yet"}</div>
        </div>
        {h.key_state && (
          <div data-testid="headline-key-state">
            <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{h.key_state.label}</div>
            <div className="mt-1 flex items-center gap-1.5 text-sm font-medium text-slate-900">
              <span className={`h-2 w-2 rounded-full ${STATE_DOT[h.key_state.state] || "bg-slate-300"}`} />
              {fmtVal(h.key_state.value)}
            </div>
          </div>
        )}
      </div>

      <div className="mt-4 flex items-start gap-2 rounded-lg bg-indigo-50 px-4 py-3" data-testid="headline-recommendation">
        <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-indigo-500" />
        <p className="text-sm text-indigo-900">{h.recommendation}</p>
      </div>

      <button onClick={expand} data-testid="headline-expand"
        className="mt-3 flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800">
        {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        {open ? "Hide" : "See all 8 dimensions"}
      </button>

      {open && dims && (
        <div className="mt-3 grid gap-2 sm:grid-cols-2" data-testid="headline-dimensions">
          {Object.entries(dims).map(([id, d]) => (
            <div key={id} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm" data-testid={`dimension-${id}`}>
              <span className={`h-2 w-2 shrink-0 rounded-full ${STATE_DOT[d.state] || "bg-slate-300"}`} title={d.state} />
              <span className="text-slate-500">{d.label}</span>
              <span className="ml-auto font-medium text-slate-900">{fmtVal(d.value)}</span>
              {d.state === "stale" && <span className="text-[10px] text-amber-600">inferred</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
